import React, { useMemo } from 'react';
import { CalendarEvent } from '../../../hooks/useSupabaseEvents';
import { useTimeFormat, useDateFormat, formatShortDate } from '../../settings/TimePicker';

interface EventTooltipProps {
  event: CalendarEvent | null;
  position: { x: number; y: number };
}

export const EventTooltip: React.FC<EventTooltipProps> = React.memo(({ event, position }) => {
  const timeFormat = useTimeFormat();
  const dateFormat = useDateFormat();

  // All hooks must be called before any early return
  const style = useMemo(() => {
    const tooltipWidth = 260; // max-w-xs ≈ 260px
    const tooltipHeight = 80; // estimate min height
    const pad = 8;

    let left = position.x;
    let top = position.y;
    let transformX = '-50%';
    let transformY = '-100%';

    // If tooltip would go above viewport, show below cursor instead
    if (top - tooltipHeight - pad < 0) {
      transformY = '8px';
    } else {
      transformY = 'calc(-100% - 8px)';
    }

    // Clamp horizontally
    if (left - tooltipWidth / 2 < pad) {
      left = pad;
      transformX = '0';
    } else if (left + tooltipWidth / 2 > window.innerWidth - pad) {
      left = window.innerWidth - pad;
      transformX = '-100%';
    }

    return {
      left: `${left}px`,
      top: `${top}px`,
      transform: `translate(${transformX}, ${transformY})`,
    };
  }, [position]);

  if (!event) return null;

  const formatTime = (t: string) => {
    const str = t.substring(0, 5);
    if (timeFormat === '24h') return str;
    const [h, m] = str.split(':').map(Number);
    const period = h >= 12 ? 'pm' : 'am';
    const h12 = h % 12 || 12;
    return `${h12}:${String(m).padStart(2, '0')}${period}`;
  };

  return (
    <div
      data-event-tooltip
      className="fixed bg-[var(--surface-elevated)] text-[var(--content-primary)] text-xs px-3 py-2 rounded-lg pointer-events-none z-50 max-w-xs border border-[var(--border-primary)]"
      style={{ ...style, boxShadow: 'var(--shadow-lg)' }}
    >
      <div className="event-title font-medium">{event.title}</div>
      <div className="text-[var(--content-secondary)] mt-1">
        {event.dates.length === 1 ?
          formatShortDate(event.dates[0], dateFormat) :
          `${formatShortDate(event.dates[0], dateFormat)} – ${formatShortDate(event.dates[event.dates.length - 1], dateFormat)}`
        } ({event.dates.length} day{event.dates.length > 1 ? 's' : ''})
        {!event.all_day && event.start_time && (
          <div className="text-xs mt-1">
            {formatTime(event.start_time)}{event.end_time ? ` – ${formatTime(event.end_time)}` : ''}
          </div>
        )}
      </div>
      {event.notes && (
        <div className="text-[var(--content-secondary)] mt-2 border-t border-[var(--border-primary)] pt-2 line-clamp-3">
          {event.notes}
        </div>
      )}
    </div>
  );
});
